/** Notification rules administration. */
import type { ColumnDef } from "@tanstack/react-table";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { useAuth } from "@/app/auth";
import { useRoles } from "@/features/admin/identity/queries";
import {
  AUDIENCES,
  CHANNELS,
  EVENT_CATALOGUE,
  type Rule,
  useDeleteRule,
  useRules,
  useSaveRule,
} from "@/features/admin/integrations/queries";
import { CompleteDataTable } from "@/shared/components/complete-data-table";
import { FormDialog } from "@/shared/components/form-dialog";
import { ErrorState, LoadingState, PageHeader } from "@/shared/components/page";
import { useForm } from "@/shared/lib/form";
import { Button } from "@/shared/ui/button";
import { Checkbox, Field, Input, Select } from "@/shared/ui/form";
import { Badge } from "@/shared/ui/primitives";

interface RuleValues extends Record<string, unknown> {
  event_type: string;
  audience: string;
  role_key: string;
  min_priority: string;
  min_severity: string;
  template_key: string;
  is_active: boolean;
}

/** Event picker grouped by aggregate with a wildcard option. */
export function EventSelect({
  id,
  value,
  onChange,
  allowWildcard,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  allowWildcard?: boolean;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <Select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">—</option>
      {allowWildcard ? <option value="*">{t("notificationRules.allEvents")}</option> : null}
      {Object.entries(EVENT_CATALOGUE).map(([group, events]) => (
        <optgroup key={group} label={group}>
          {allowWildcard ? <option value={`${group}.*`}>{`${group}.*`}</option> : null}
          {events.map((ev) => (
            <option key={ev} value={ev}>
              {ev}
            </option>
          ))}
        </optgroup>
      ))}
    </Select>
  );
}

/** Rule create/edit dialog. */
export function RuleDialog({ rule, onClose }: { rule?: Rule; onClose: () => void }): ReactElement {
  const { t } = useTranslation();
  const save = useSaveRule();
  const roles = useRoles();
  const [channels, setChannels] = useState<string[]>(rule?.channels ?? ["in_app"]);
  const cond = (rule?.conditions ?? {}) as {
    role_key?: string;
    min_priority?: string;
    min_severity?: string;
  };
  const initial = useMemo<RuleValues>(
    () => ({
      event_type: rule?.event_type ?? "",
      audience: rule?.audience ?? "assignees",
      role_key: cond.role_key ?? "",
      min_priority: cond.min_priority ?? "",
      min_severity: cond.min_severity ?? "",
      template_key: rule?.template_key ?? "",
      is_active: rule?.is_active ?? true,
    }),
    [rule, cond.role_key, cond.min_priority, cond.min_severity],
  );
  const form = useForm<RuleValues>({
    initial,
    schema: z
      .object({
        event_type: z.string().min(1, t("app.required")),
        audience: z.string().min(1),
        role_key: z.string(),
      })
      .refine((v) => v.audience !== "role" || v.role_key, {
        message: t("app.required"),
        path: ["role_key"],
      }),
    idPrefix: "rule",
  });
  const submit = async (): Promise<void> => {
    const ok = await form.submit(async (v) => {
      const conditions: Record<string, string> = {};
      if (v.audience === "role") {
        conditions.role_key = v.role_key;
      }
      if (v.min_priority) {
        conditions.min_priority = v.min_priority;
      }
      if (v.min_severity) {
        conditions.min_severity = v.min_severity;
      }
      await save.mutateAsync({
        id: rule?.id,
        body: {
          event_type: v.event_type,
          audience: v.audience,
          channels,
          conditions,
          template_key: v.template_key,
          is_active: v.is_active,
        },
      });
    });
    if (ok) {
      onClose();
    }
  };
  return (
    <FormDialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={rule ? t("notificationRules.edit") : t("notificationRules.new")}
      onSubmit={submit}
      submitting={form.submitting}
      error={form.errors._}
    >
      {rule?.is_system ? (
        <p className="rounded-md bg-amber-50 p-2 text-xs text-amber-900">
          {t("notificationRules.systemNote")}
        </p>
      ) : null}
      <Field
        id="rule-event_type"
        label={t("notificationRules.event")}
        error={form.errors.event_type}
      >
        <EventSelect
          id="rule-event_type"
          value={form.values.event_type}
          onChange={(v) => form.set("event_type", v)}
          allowWildcard
        />
      </Field>
      <Field
        id="rule-audience"
        label={t("notificationRules.audience")}
        hint={t(`notificationRules.audienceHints.${form.values.audience}`, { defaultValue: "" })}
      >
        <Select
          id="rule-audience"
          value={form.values.audience}
          onChange={(e) => form.set("audience", e.target.value)}
        >
          {AUDIENCES.map((a) => (
            <option key={a} value={a}>
              {t(`notificationRules.audiences.${a}`)}
            </option>
          ))}
        </Select>
      </Field>
      {form.values.audience === "role" ? (
        <Field id="rule-role_key" label={t("members.roles")} error={form.errors.role_key}>
          <Select
            id="rule-role_key"
            value={form.values.role_key}
            onChange={(e) => form.set("role_key", e.target.value)}
          >
            <option value="">—</option>
            {(roles.data ?? []).map((r) => (
              <option key={r.key} value={r.key}>
                {r.name}
              </option>
            ))}
          </Select>
        </Field>
      ) : null}
      <Field id="rule-channels" label={t("notificationRules.channels")}>
        <div className="flex flex-wrap gap-4" role="group">
          {CHANNELS.map((c) => (
            <Checkbox
              key={c}
              id={`rule-channel-${c}`}
              label={t(`notificationRules.channelNames.${c}`)}
              checked={channels.includes(c)}
              onChange={(e) =>
                setChannels(e.target.checked ? [...channels, c] : channels.filter((x) => x !== c))
              }
            />
          ))}
        </div>
      </Field>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field
          id="rule-min_priority"
          label={t("notificationRules.minPriority")}
          hint={t("notificationRules.minPriorityHint")}
        >
          <Select
            id="rule-min_priority"
            value={form.values.min_priority}
            onChange={(e) => form.set("min_priority", e.target.value)}
          >
            <option value="">{t("app.all")}</option>
            {["normal", "high", "urgent"].map((p) => (
              <option key={p} value={p}>
                {t(`workOrders.priority.${p}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field id="rule-min_severity" label={t("notificationRules.minSeverity")}>
          <Select
            id="rule-min_severity"
            value={form.values.min_severity}
            onChange={(e) => form.set("min_severity", e.target.value)}
          >
            <option value="">{t("app.all")}</option>
            {["medium", "high", "critical"].map((p) => (
              <option key={p} value={p}>
                {t(`requests.severity.${p}`)}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <Field
        id="rule-template_key"
        label={t("notificationRules.template")}
        hint={t("notificationRules.templateHint")}
      >
        <Input {...form.field("template_key")} />
      </Field>
      <Checkbox
        id="rule-is_active"
        label={t("app.active")}
        checked={form.values.is_active}
        onChange={(e) => form.set("is_active", e.target.checked)}
      />
    </FormDialog>
  );
}

/** Rules page. */
export function NotificationRulesPage(): ReactElement {
  const { t } = useTranslation();
  const { can } = useAuth();
  const rules = useRules();
  const remove = useDeleteRule();
  const [dialog, setDialog] = useState<{ rule?: Rule } | null>(null);
  const manage = can("notifications.rule.manage");
  const columns: ColumnDef<Rule>[] = [
    {
      id: "event",
      header: t("notificationRules.event"),
      cell: (c) => <span className="font-mono text-xs">{c.row.original.event_type}</span>,
    },
    {
      id: "audience",
      header: t("notificationRules.audience"),
      cell: (c) => (
        <span>
          {t(`notificationRules.audiences.${c.row.original.audience}`)}
          {(c.row.original.conditions as { role_key?: string }).role_key
            ? ` (${(c.row.original.conditions as { role_key: string }).role_key})`
            : ""}
        </span>
      ),
    },
    {
      id: "channels",
      header: t("notificationRules.channels"),
      cell: (c) => (
        <span className="flex gap-1">
          {(c.row.original.channels ?? []).map((ch) => (
            <Badge key={ch} tone="outline">
              {t(`notificationRules.channelNames.${ch}`)}
            </Badge>
          ))}
        </span>
      ),
    },
    {
      id: "conditions",
      header: t("notificationRules.conditions"),
      cell: (c) => {
        const k = Object.entries(c.row.original.conditions ?? {}).filter(
          ([key]) => key !== "role_key",
        );
        return k.length ? k.map(([a, b]) => `${a}=${String(b)}`).join(", ") : "—";
      },
    },
    {
      id: "status",
      header: t("app.status"),
      cell: (c) => (
        <span className="flex gap-1">
          {c.row.original.is_active ? (
            <Badge tone="success">{t("app.active")}</Badge>
          ) : (
            <Badge>{t("app.inactive")}</Badge>
          )}
          {c.row.original.is_system ? <Badge tone="outline">{t("roles.system")}</Badge> : null}
        </span>
      ),
    },
    ...(manage
      ? [
          {
            id: "actions",
            header: t("app.actions"),
            cell: (c): ReactElement => (
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setDialog({ rule: c.row.original })}
                >
                  {t("app.edit")}
                </Button>
                {!c.row.original.is_system ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive"
                    onClick={() => {
                      if (globalThis.confirm(t("app.confirmDelete"))) {
                        remove.mutate(c.row.original.id);
                      }
                    }}
                  >
                    {t("app.delete")}
                  </Button>
                ) : null}
              </div>
            ),
          } satisfies ColumnDef<Rule>,
        ]
      : []),
  ];
  return (
    <div>
      <PageHeader
        title={t("notificationRules.title")}
        subtitle={t("notificationRules.subtitle")}
        actions={
          manage ? (
            <Button onClick={() => setDialog({})}>{t("notificationRules.new")}</Button>
          ) : null
        }
      />
      {dialog ? <RuleDialog rule={dialog.rule} onClose={() => setDialog(null)} /> : null}
      {rules.isPending ? (
        <LoadingState />
      ) : rules.isError ? (
        <ErrorState error={rules.error} onRetry={() => void rules.refetch()} />
      ) : (
        <CompleteDataTable
          columns={columns}
          data={rules.data ?? []}
          getRowId={(rule) => rule.id}
          getSearchText={(rule) => rule.event_type}
          searchDescription={t("tablePilot.searchFields", { fields: t("notificationRules.event") })}
          sortFields={[
            {
              value: "event",
              label: t("notificationRules.event"),
              valueOf: (rule) => rule.event_type,
            },
          ]}
          minWidth={1000}
        />
      )}
    </div>
  );
}
